import * as React from 'react';
import { isEqual } from 'lodash-es';

import { constantTable } from '../../../helpers/constants';
import { DataPager } from "../../../components/datapagers/DataPager";
import { NoteCard } from "../../notes/components/NoteCard";
import { NoteForm } from "../../notes/components/NoteForm";
import { NotesFilters } from "../../notes/components/NotesFilters";
import { PagedList } from "../../../types/dataQueries/base/PagedList";
import { PDFNote } from '../../notes/models/PDFNote';
import { PDFNoteQuery } from '../../notes/models/PDFNoteQuery';
import { services } from '../../../services';
import { useBackgroundPromiseEffectByCondition } from '../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { Dialog } from 'primereact/dialog';
import { DataStorage } from '../../../helpers/utils/DataStorage';
import { ProjectDoc } from '../../projectDocs/models/ProjectDoc';
import { NoteUpdatesController } from '../helpers/noteUpdates';
import { NotesPagerWithUpdates } from './NotesPagerWithUpdates';


export enum NotesPagerViewType {
  Default = 1,
  Prev = 2,
  Subordinates = 3
}

const getActivePDFNoteQuery = (query: PDFNoteQuery): PDFNoteQuery => ({
  ...query,
  IsDeleted: false,
  IncludeDeletedWorkflowInstances: false,
});

export interface NotesPaging {
  offset: number;
  onOffsetChange(offset: number, replace?: boolean): void;
}

export interface NotesPagerProps {
  dataStorage: DataStorage;
  setDataStorageValue: (key: string, value: any) => void;
  initialQueryName: string;
  initialQuery: PDFNoteQuery;
  projectDocFolderStatusId: number;
  projectDoc?: ProjectDoc;
  requestEnable: boolean;
  onNoteChanged: () => void;
  type: "Recived" | "Completed" | "Archived";
  noteUpdates?: NoteUpdatesController;
  paging?: NotesPaging;
}

export const NotesPager: React.FC<NotesPagerProps> = (props) => props.noteUpdates
  ? <NotesPagerWithUpdates {...props} noteUpdates={props.noteUpdates} />
  : <LegacyNotesPager {...props} />;

const LegacyNotesPager: React.FC<NotesPagerProps> = ({ dataStorage, setDataStorageValue,  initialQueryName, initialQuery, projectDocFolderStatusId, projectDoc, requestEnable, onNoteChanged, type }) => {
  const taskNotesKey: string = `${initialQueryName}_NotesPager_taskNotes`;
  const taskNotesQueryKey: string = `${initialQueryName}_NotesPager_taskNotesQuery`;
  const skipCountKey: string = 'NotesPager_Skip';


  const [taskNotes, setTaskNotes] = React.useState<PagedList<PDFNote>>(dataStorage.get<PagedList<PDFNote>>(taskNotesKey, constantTable.initialPagedList));
  const [taskNotesQuery, setTaskNotesQuery] = React.useState<PDFNoteQuery>(getActivePDFNoteQuery(dataStorage.get<PDFNoteQuery>(taskNotesQueryKey, initialQuery)));

  const clearNotesList = React.useCallback(() => {
    setTaskNotes({ ...constantTable.initialPagedList });
    setDataStorageValue(taskNotesKey, undefined);
  }, [setDataStorageValue, taskNotesKey]);

  const updateQuery = (newQuery: PDFNoteQuery) => {
    const newQ = getActivePDFNoteQuery({ ...taskNotesQuery, ...newQuery });
    if (!isEqual(taskNotesQuery, newQ)) {
      // Сразу убираем старые карточки — иначе до ответа API видно чужой раздел/страницу.
      clearNotesList();
      setDataStorageValue(taskNotesQueryKey, newQ);
      setDataStorageValue(initialQueryName, newQ);
      setTaskNotesQuery(newQ);
      setDataStorageValue(skipCountKey, newQ.Skip);
    }
  }; 

  React.useEffect(() => {
    initialQuery.Skip = initialQuery.Skip;
    updateQuery({ ...initialQuery });
    // eslint-disable-next-line
  }, [initialQuery]);

  const setCurrentCodeId = (codeId: number) => {
    updateQuery({
      CodeIds: codeId > 0 ? [codeId] : []
    } as PDFNoteQuery);
  }

  const isLoading = useBackgroundPromiseEffectByCondition(
    () => requestEnable && !dataStorage.hasValue(taskNotesKey),
    () => services.pDFNoteService.fetchPaged(taskNotesQuery),
    (res: PagedList<PDFNote>) => {
      setDataStorageValue(taskNotesKey, res);
      setTaskNotes(res);
      if (res.Items.length == 0 && taskNotesQuery.Skip != undefined && taskNotesQuery.Skip > 0) {
        updateQuery({
          Skip: taskNotesQuery.Skip - constantTable.paginatorRows
        } as PDFNoteQuery);
      }
    },
    [taskNotesQuery, dataStorage],
    true // локальный оверлей в DataPager, без глобальной полоски сверху
  );

  const removeNote = React.useCallback((noteId: number) => {
    setTaskNotes(prev => {
      const newItems = prev.Items.filter(n => n.Id !== noteId);
      if (newItems.length === 0 && taskNotesQuery.Skip != null && taskNotesQuery.Skip > 0) {
        updateQuery({ Skip: taskNotesQuery.Skip - constantTable.paginatorRows } as PDFNoteQuery);
      }
      return { ...prev, Items: newItems, TotalCount: Math.max(0, prev.TotalCount - 1) };
    });
    onNoteChanged();
  // eslint-disable-next-line
  }, [taskNotesQuery, onNoteChanged]);

  // Treat the window before the fetch effect fires as loading too, so the empty
  // "Нет данных" text never flashes while a request is pending.
  const loading = isLoading || (requestEnable && !dataStorage.hasValue(taskNotesKey));

  const [formDialogIsVisible, setFormDialogIsVisible] = React.useState<boolean>(false);
  const [formDialogItemId, setFormDialogItemId] = React.useState<number>(0);

  const onFormLinkClick = (id: number) => {
    setFormDialogIsVisible(true);
    setFormDialogItemId(id);
  };


  return (
    <React.Fragment>
      <Dialog 
        header="Просмотр замечания"
        onHide={() => setFormDialogIsVisible(false)}
        visible={formDialogIsVisible}
        maximized={true}
        className="note-view-dialog"
        modal={true}
        closable={true}
        >
        <NoteForm
          id={formDialogItemId}
          projectDoc={projectDoc}
          onNoteChanged={e => { }} />
      </Dialog>
      <div className="notes-list">
        <NotesFilters
          codeFilterId={taskNotesQuery.CodeIds.length > 0 ? taskNotesQuery.CodeIds[0] : 0}
          onCodeFilterChange={e => setCurrentCodeId(e)}
          projectDocTypeId={projectDoc?.DocTypeId}
          totalCount={taskNotes.TotalCount} />
        <DataPager
          data={taskNotes}
          query={taskNotesQuery}
          updateQuery={updateQuery}
          loading={loading}
          itemTemplate={(e) => (
            <NoteCard
              pdfNote={e.item}
              projectDocFolderStatusId={projectDocFolderStatusId}
              projectDoc={projectDoc}
              onNoteChanged={() => removeNote(e.item.Id ?? 0)}
              onFormLinkClick={() => onFormLinkClick(e.item.Id ?? 0)}  />
          )} />
      </div>
    </React.Fragment>
  );
}
